import {
  bold,
  bulletedList,
  cyan,
  formatTime,
  green,
  darkGrey,
  indent,
  lightCyan,
  plural,
  red,
  prefixCharacters,
  yellow,
} from '@ms-cloudpack/task-reporter';
import type { InitOptions } from './types/InitOptions.js';
import type { InitPackageResult } from './types/InitPackageResult.js';
import type { InitSummary } from './types/InitSummary.js';
import { formatLocation } from '@ms-cloudpack/path-utilities';
import type { GeneratedChange, RequiredGeneratedChangeType } from './types/GeneratedChange.js';

export interface FormatSummaryParams extends Pick<InitOptions, 'check'> {
  summary: InitSummary;
  isMultiApp?: boolean;
  verb: 'init' | 'bundle';
  isInterrupted?: boolean;
}

/** Result types with different colors and symbols for logging purposes */
type ResultType = 'error' | 'warn' | 'add' | 'success';
const colorsByType: Record<ResultType, (str: string) => string> = {
  error: red,
  warn: yellow,
  add: (str) => str, // no color
  success: green,
};
const statusByType: Record<ResultType, string> = {
  error: prefixCharacters.error,
  warn: prefixCharacters.warn,
  add: '+',
  success: prefixCharacters.complete,
};

/** "cloudpack.generated.json" with color */
const generatedConfigFile = cyan('cloudpack.generated.json');
/** "cloudpack init" with color and quotes */
const cloudpackInit = `"${yellow('cloudpack init')}"`;

/**
 * Given a summary and options, returns a formatted string message to display to the user via console.
 */
export function formatInitSummary(params: FormatSummaryParams): string {
  const { summary, check, isInterrupted, isMultiApp, verb } = params;
  /** Sections of the summary that will be joined with two newlines */
  const summarySections: string[] = [];

  const {
    totalPackages,
    totalErrors,
    totalWarnings,
    totalChanges,
    totalRequiredChanges,
    packagesWithErrors,
    packagesWithWarnings,
    generatedFileUpdates,
    deletedGeneratedConfig,
  } = summary;

  // With --check, show required changes even if there are errors.
  // Otherwise, skip since they'll be applied automatically once errors are fixed.
  const showRequiredChanges = !!(totalRequiredChanges && (!totalErrors || check));

  const incompleteText = isInterrupted ? bold(` (incomplete)`) : ``;

  let warnings: string | undefined;
  let generatedChanges: string | undefined;

  // In single-app mode, report packages with warnings.
  if (!isMultiApp && packagesWithWarnings.length) {
    warnings = formatPackageResults({ results: packagesWithWarnings, issueType: 'warn' });
  }

  // Report required changes if appropriate (see showRequiredChanges)
  if (showRequiredChanges) {
    generatedChanges = formatPackageUpdates({ generatedFileUpdates, check, hasErrors: !!totalErrors });
  }

  if (check) {
    // With --check, it's most helpful to show warnings then generated changes. Required changes
    // are errors in that context, and if they're listed above warnings, it's easy to miss them,
    // which might lead to confusion.
    warnings && summarySections.push(warnings);
    generatedChanges && summarySections.push(generatedChanges);
  } else {
    // Without --check, show generated changes first, then warnings. In this case, any generated
    // change listing is just informational (the changes were already made), but the warnings
    // require manual intervention if the user wants to fix them.
    generatedChanges && summarySections.push(generatedChanges);
    warnings && summarySections.push(warnings);
  }

  // Report packages with errors.
  if (packagesWithErrors.length) {
    summarySections.push(formatPackageResults({ results: packagesWithErrors, issueType: 'error' }));
  }

  if (deletedGeneratedConfig) {
    summarySections.push(`Deleted the ${generatedConfigFile} file because it is no longer needed.`);
  }

  if (totalPackages > 1 || verb === 'init') {
    // Report totals
    const action = verb === 'init' ? 'evaluated' : 'bundled';
    const totalLines = [
      `${cyan(plural(totalPackages, 'total package'))} ${action} in ${formatTime(summary.duration)}${incompleteText}`,
    ];

    const errorPkgCount = packagesWithErrors.length;
    const successPkgCount = totalPackages - errorPkgCount;
    const warningPkgCount = packagesWithWarnings.length;
    const optionalChangesCount = totalChanges - totalRequiredChanges;

    // See snapshots for examples of this output
    totalLines.push(
      ...([
        // "without errors" because they might have warnings
        formatCount('success', successPkgCount, 'package', `${action} without errors`),
        !errorPkgCount &&
          (check
            ? // For --check, only show optional changes if there are no errors, and format the
              // optional changes as warnings. (required changes will be shown below)
              formatCount('warn', optionalChangesCount, 'optional change', 'could be made to generated config')
            : // If updating, show the total changes made.
              formatCount('add', totalChanges, 'change', 'made to generated config')),
        formatCount('warn', warningPkgCount, 'package', 'with warnings', totalWarnings, 'total warning'),
        // For --check, show required changes as errors.
        check && formatCount('error', totalRequiredChanges, 'required change', 'missing from generated config'),
        formatCount('error', errorPkgCount, 'package', 'with errors', totalErrors, 'total error'),
      ].filter(Boolean) as string[]),
    );

    if (totalLines.length > 1) {
      // add a colon to the first line if there are other lines
      totalLines[0] += ':';
    }

    summarySections.push(totalLines.join('\n'));
  }

  if (isInterrupted) {
    summarySections.push(yellow(bold('Process was interrupted before all tasks could complete.')));
  } else if (check && totalRequiredChanges && !totalErrors) {
    // Skip the instruction to update the generated config if there were errors.
    summarySections.push(
      `Run ${cloudpackInit} in ${summary.appPath} to apply missing changes to the ${generatedConfigFile} file.`,
    );
  }

  // Skip the header if there was nothing to report.
  if (summarySections.length) {
    // For bundle, we just say "summary" since `appPath` might not actually be the path that was bundled.
    summarySections.unshift(bold(`==== ${verb === 'bundle' ? 'Summary' : `Summary for ${summary.appPath}`} ====`));
  }

  const summaryString = summarySections
    .join('\n\n')
    // replace >= 3 newlines with 2 newlines
    .replace(/\n{3,}/g, '\n\n');
  // Ensure a final newline for readability, unless it's empty.
  return !summaryString || summaryString.endsWith('\n') ? summaryString : summaryString + '\n';
}

/**
 * If `count > 0`, return a pluralized formatted string like "+ 5 items description"
 * (otherwise ignore). `type` determines the status character used, and the color for the
 * status character and item count.
 *
 * `extraCount` and `extraItem` are optional and will be added to the end in parentheses
 * if both are provided.
 */
function formatCount(
  type: ResultType,
  count: number,
  item: string,
  description: string,
  extraCount?: number,
  extraItem?: string,
): string | undefined {
  if (count > 0) {
    const color = colorsByType[type];
    const statusChar = bold(color(statusByType[type]));
    const itemCount = color(plural(count, item));
    const extra = extraCount && extraItem ? ` (${plural(extraCount, extraItem)})` : '';
    return `  ${statusChar} ${itemCount} ${description}${extra}`;
  }
  return undefined;
}

function formatPackageUpdates(
  params: Pick<InitSummary, 'generatedFileUpdates'> &
    Pick<FormatSummaryParams, 'isInterrupted' | 'check'> & {
      hasErrors: boolean;
    },
): string {
  const { generatedFileUpdates, check, isInterrupted, hasErrors } = params;

  // mapping from change type to [description, optional plural description]
  const descriptions: Record<RequiredGeneratedChangeType, [string, string?]> = {
    'added-export': [`path$s ${check ? 'missing from' : 'added to'} exports map`],
    'included-dependency-required': ['dependency required to be included', 'dependencies required to be included'],
    'changed-export': ['previously unused export path$s now used'],
    'added-dynamic-import': ['dynamic import$s added'],
    'removed-excluded-dependency-required': ['dependency is no longer excluded', 'dependencies are no longer excluded'],
  };

  const summaryString: string[] = [];

  for (const packageUpdates of generatedFileUpdates) {
    // list of update type descriptions and updates of that type
    const updates: [string, GeneratedChange[]][] = [];
    for (const type of Object.keys(descriptions) as RequiredGeneratedChangeType[]) {
      const typeUpdates = packageUpdates.changes.filter((u) => u.type === type);

      if (typeUpdates.length) {
        const description = plural(typeUpdates.length, ...descriptions[type]);
        updates.push([description, typeUpdates]);
      }
    }

    if (updates.length) {
      summaryString.push(formatPackageHeader(packageUpdates));
      for (const [description, typeUpdates] of updates) {
        summaryString.push(
          // If updates are required, format them as errors.
          formatPackageSubhead({ issueType: check ? 'error' : 'add', description }),
          bulletedList(typeUpdates.map((u) => `${cyan(u.change)}${u.reason ? ` - ${u.reason}` : ''}`)),
        );
      }
      summaryString.push('');
    }
  }

  if (summaryString.length) {
    const needsUpdates = check || isInterrupted;
    const extraInstructions = needsUpdates
      ? hasErrors
        ? `fix errors first, then run ${cloudpackInit}`
        : `run ${cloudpackInit} to fix`
      : '';
    summaryString.unshift(
      bold(`Updates ${needsUpdates ? 'needed for' : 'made to'} ${generatedConfigFile}`) +
        `${extraInstructions ? ` (${extraInstructions})` : ''}:\n`,
    );
  }
  return summaryString.join('\n');
}

/** Helper to format `packagesWithWarnings` or `packagesWithErrors`. */
function formatPackageResults(params: { results: InitPackageResult[]; issueType: 'error' | 'warn' }): string {
  const { results, issueType } = params;
  if (!results.length) {
    return '';
  }

  const color = colorsByType[issueType];
  const messageType = issueType === 'error' ? 'errors' : 'warnings';
  const statusChar = bold(color(prefixCharacters[issueType]));

  const summaryString = [bold(color(`${plural(results.length, 'package')} with ${messageType}:`)), ''];

  for (const result of results) {
    const unformattedMessages = result[messageType];
    if (!unformattedMessages?.length) {
      continue;
    }

    const description = plural(unformattedMessages.length, messageType.slice(0, -1) + '$s');
    summaryString.push(formatPackageHeader(result), formatPackageSubhead({ issueType, description }));

    const messages = unformattedMessages.map(({ location, text, source }) => {
      // Show the whole path so it can be easily opened with ctrl+click.
      // NOTE: location.file should be relative to the package path, not the app path.
      const filePath = location ? ` ${formatLocation({ ...location, fromPath: result.path })}` : '';
      // Indent the message for readability.
      return `[${source}]${filePath}\n${color(indent(text, 1))}`;
    });

    summaryString.push(bulletedList(messages, undefined, statusChar), '');
  }

  return summaryString.join('\n');
}

/**
 * Helper to format the package `name@version` and output path.
 */
function formatPackageHeader(params: InitPackageResult): string {
  const { name, version, path: packagePath, outputPath } = params;
  const packageName = bold(lightCyan(`${name}@${version}`));

  return `${packageName} - ${packagePath}${outputPath ? '\n' + darkGrey(`(output: ${outputPath})`) : ''}`;
}

/**
 * Add a status character and formatting to the subhead description.
 */
function formatPackageSubhead(params: { description: string; issueType: ResultType }): string {
  const { description, issueType } = params;
  const color = colorsByType[issueType];
  const statusChar = bold(color(statusByType[issueType]));
  return `${statusChar} ${bold(color(description))}`;
}
